import { useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import { DesignSystemRecipe, DesignSystemToken, TokenCategory } from 'designSystem/types';
import SpacingScale from '../tokens/SpacingScale';
import ElevationScale from '../tokens/ElevationScale';
import BorderRadiusScale from '../tokens/BorderRadiusScale';
import OpacityScale from '../tokens/OpacityScale';
import TokenGrid from '../tokens/TokenGrid';
import FilterPills from '../FilterPills';
import * as Base from '../styles';
import * as S from './styles';

const STYLES_CATEGORY_ORDER: { key: TokenCategory; label: string }[] = [
	{ key: 'spacing', label: 'Spacing' },
	{ key: 'elevation', label: 'Elevation' },
	{ key: 'borderRadius', label: 'Border Radius' },
	{ key: 'opacity', label: 'Opacity' },
	{ key: 'motion', label: 'Motion' },
	{ key: 'other', label: 'Other' },
];

const StylesView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, selectedDsPicks, selectedCategories, activeTab } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			selectedDsPicks: r.mode.selectedDsPicks,
			selectedCategories: r.mode.designSystemFilters.styles.selectedCategories,
			activeTab: selectActiveDsTab(r.mode),
		}),
		shallowEqual
	);
	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	const tokensByCategory = useMemo(() => {
		const result: Record<string, { theme: DesignSystemToken[]; styles: DesignSystemToken[] }> = {};
		for (const { key } of STYLES_CATEGORY_ORDER) {
			const themeTokens = (designSystemContent?.theme?.byCategory?.[key] ?? []).filter(
				(t) => !t.mode
			);
			const stylesTokens = (designSystemContent?.styles?.tokens?.byCategory?.[key] ?? []).filter(
				(t) => !t.mode
			);
			result[key] = { theme: themeTokens, styles: stylesTokens };
		}
		return result;
	}, [designSystemContent?.theme, designSystemContent?.styles?.tokens]);

	const recipesByDomain = useMemo(() => {
		const all = designSystemContent?.styles?.recipes?.byDomain ?? {};
		// Drop the 'type' domain — those recipes live on the Typography tab
		const filtered: Record<string, DesignSystemRecipe[]> = {};
		for (const [domain, recipes] of Object.entries(all)) {
			if (domain === 'type') continue;
			filtered[domain] = recipes;
		}
		return filtered;
	}, [designSystemContent?.styles?.recipes]);

	const recipeDomains = useMemo(() => Object.keys(recipesByDomain).sort(), [recipesByDomain]);

	const availableCategories = useMemo(
		() =>
			STYLES_CATEGORY_ORDER.map(({ key, label }) => ({
				value: key,
				label,
				count:
					(tokensByCategory[key]?.theme.length ?? 0) + (tokensByCategory[key]?.styles.length ?? 0),
			})).filter((c) => c.count > 0),
		[tokensByCategory]
	);

	const visibleCategories = useMemo(() => {
		const keys = STYLES_CATEGORY_ORDER.map((c) => c.key);
		if (selectedCategories.length === 0) return keys;
		const selected = new Set(selectedCategories);
		return keys.filter((k) => selected.has(k));
	}, [selectedCategories]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const isRecipeSelected = (className: string) =>
		selectedDsPicks.some((p) => p.kind === 'recipe' && p.className === className);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderScale = (category: TokenCategory, tokens: DesignSystemToken[]) => {
		if (tokens.length === 0) return null;
		switch (category) {
			case 'spacing':
				return <SpacingScale tokens={tokens} />;
			case 'elevation':
				return <ElevationScale tokens={tokens} />;
			case 'borderRadius':
				return <BorderRadiusScale tokens={tokens} />;
			case 'opacity':
				return <OpacityScale tokens={tokens} />;
			default:
				return <TokenGrid tokens={tokens} />;
		}
	};

	const renderCategorySection = (categoryKey: TokenCategory) => {
		const buckets = tokensByCategory[categoryKey];
		if (!buckets) return null;
		if (buckets.theme.length === 0 && buckets.styles.length === 0) return null;

		const label = STYLES_CATEGORY_ORDER.find((c) => c.key === categoryKey)?.label ?? categoryKey;
		const hasBoth = buckets.theme.length > 0 && buckets.styles.length > 0;

		return (
			<S.CategorySection key={categoryKey}>
				<Base.SectionDivider>{label}</Base.SectionDivider>
				{hasBoth ? (
					<>
						<S.BandHeader>Color Tokens</S.BandHeader>
						{renderScale(categoryKey, buckets.theme)}
						<S.BandHeader>Semantic Colors</S.BandHeader>
						{renderScale(categoryKey, buckets.styles)}
					</>
				) : (
					renderScale(categoryKey, buckets.theme.length > 0 ? buckets.theme : buckets.styles)
				)}
			</S.CategorySection>
		);
	};

	const buildRecipeStyle = (recipe: DesignSystemRecipe): React.CSSProperties => {
		const style: Record<string, string> = {};
		for (const prop of recipe.properties) {
			const camelCase = prop.property.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
			style[camelCase] = prop.value;
		}
		return style as React.CSSProperties;
	};

	// Container-like recipes (surfaces, panels) have no padding / font-size /
	// width / height of their own — an empty <div> with just background+border
	// collapses to invisible. Force a min size in that case so the surface
	// actually renders. Inline elements (badges, type) size themselves from
	// their own properties and shouldn't be inflated.
	const SIZE_GIVING_PROPS = new Set([
		'padding',
		'padding-top',
		'padding-bottom',
		'padding-left',
		'padding-right',
		'font-size',
		'width',
		'height',
		'min-width',
		'min-height',
	]);
	const recipeNeedsForcedSize = (recipe: DesignSystemRecipe): boolean =>
		!recipe.properties.some((p) => SIZE_GIVING_PROPS.has(p.property));

	const renderRecipeCard = (recipe: DesignSystemRecipe) => {
		const selected = isRecipeSelected(recipe.className);
		const togglePick = () =>
			dispatch(toggleDsPick({ kind: 'recipe', className: recipe.className }));
		return (
			<S.RecipeCard
				key={recipe.id}
				$selected={selected}
				role="button"
				tabIndex={0}
				aria-pressed={selected}
				onClick={togglePick}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						togglePick();
					}
				}}
			>
			<S.RecipePreviewArea>
				<S.RecipePreviewElement
					$forceSize={recipeNeedsForcedSize(recipe)}
					style={buildRecipeStyle(recipe)}
				>
					{recipe.name}
				</S.RecipePreviewElement>
			</S.RecipePreviewArea>
			<S.RecipeMeta>
				<S.RecipeHeader>
					<S.RecipeName>{recipe.name}</S.RecipeName>
				</S.RecipeHeader>
				<S.RecipeClassName>{recipe.className}</S.RecipeClassName>
				{recipe.description && <S.RecipeDescription>{recipe.description}</S.RecipeDescription>}
			</S.RecipeMeta>
			<S.RecipeTooltip>
				{[
					`${recipe.className} {`,
					...recipe.properties.map((p) => `  ${p.property}: ${p.value};`),
					'}',
				].join('\n')}
			</S.RecipeTooltip>
		</S.RecipeCard>
		);
	};

	const hasAnyTokens = availableCategories.length > 0;
	const hasRecipes = recipeDomains.length > 0;
	const isEmpty = !hasAnyTokens && !hasRecipes;

	return (
		<S.ScrollContainer ref={scrollRef}>
			<S.HeaderArea>
				<Base.SectionHeader>
					<Base.SectionTitle>Styles</Base.SectionTitle>
				</Base.SectionHeader>
			</S.HeaderArea>
			{hasAnyTokens && <FilterPills section="styles" categories={availableCategories} sticky />}
			<Base.SectionContent>
				{isEmpty && <p>No style tokens or recipes found.</p>}
				{visibleCategories.map((cat) => renderCategorySection(cat))}
				{hasRecipes && (
					<>
						{hasAnyTokens && <Base.SectionDivider>Recipes</Base.SectionDivider>}
						{recipeDomains.map((domain) => (
							<S.RecipeDomainSection key={domain}>
								<S.DomainHeader>{domain}</S.DomainHeader>
								<S.RecipeGrid>{recipesByDomain[domain].map(renderRecipeCard)}</S.RecipeGrid>
							</S.RecipeDomainSection>
						))}
					</>
				)}
			</Base.SectionContent>
		</S.ScrollContainer>
	);
};

export default StylesView;
